import Papa from "papaparse";
import * as XLSX from "xlsx";
import { sha256 as nobleSha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
import type { ColumnMapping, ImportAdapter, ParsedFile } from "./ImportAdapter";
import { normalizeHeader } from "./ImportAdapter";
import type { ImportBatch } from "../types";

async function sha256(buffer: ArrayBuffer): Promise<string> {
  if (crypto.subtle?.digest) {
    const digest = await crypto.subtle.digest("SHA-256", buffer);
    return bytesToHex(new Uint8Array(digest));
  }
  return bytesToHex(nobleSha256(new Uint8Array(buffer)));
}

export class GenericAdapter implements ImportAdapter {
  readonly source: ImportBatch["source"] = "generico";
  readonly automaticMapping: boolean = false;

  async parse(file: File): Promise<ParsedFile> {
    const buffer = await file.arrayBuffer();
    const extension = file.name.split(".").pop()?.toLowerCase();
    let rows: Record<string, unknown>[] = [];
    if (extension === "csv") {
      const result = Papa.parse<Record<string, unknown>>(new TextDecoder("utf-8").decode(buffer), { header: true, skipEmptyLines: true, transformHeader: (header) => header.trim() });
      if (result.errors.length && !result.data.length) throw new Error(result.errors[0].message);
      rows = result.data;
    } else if (extension === "xls" || extension === "xlsx") {
      const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
      rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[workbook.SheetNames[0]], { defval: "" });
    } else throw new Error("Formato não suportado. Envie CSV, XLS ou XLSX.");
    const headers = rows.length ? Object.keys(rows[0]) : [];
    if (!headers.length) throw new Error("O arquivo não possui colunas reconhecíveis.");
    return { fileName: file.name, fileHash: await sha256(buffer), headers, rows };
  }

  suggestMapping(headers: string[]): ColumnMapping {
    const match = (patterns: RegExp[]) => headers.find((header) => patterns.some((pattern) => pattern.test(normalizeHeader(header)))) ?? "";
    return {
      sku: match([/^SKU$/, /CODIGO/, /REFERENCIA/]),
      color: match([/COR/, /VARIACAO/, /VARIANT/]),
      quantity: match([/QUANTIDADE/, /^QTD$/, /UNIDADES/]),
      order: match([/PEDIDO/, /ORDER/]),
      title: match([/^TITULO$/, /NOMEDOPRODUTO/, /NOMEDOITEM/, /ANUNCIO/, /DESCRICAODOITEM/]),
    };
  }
}
